import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dismiss20Regular } from "@fluentui/react-icons";
import {
  ApiError,
  createWorkType,
  deleteWorkType,
  getWorkTypes,
  updateWorkType,
  type WorkType,
} from "../../../clients/clientsApi";

export function WorkTypesTab() {
  const navigate = useNavigate();
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [nome, setNome] = useState("");

  const [editing, setEditing] = useState<WorkType | null>(null);
  const [draftName, setDraftName] = useState("");

  const loadWorkTypes = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setWorkTypes(await getWorkTypes());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os tipos de obra.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkTypes();
  }, [loadWorkTypes]);

  const handleAdd = async () => {
    if (isSaving) return;
    if (!nome.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createWorkType({ name: nome.trim() });
      setWorkTypes((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
      setNome("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar o tipo de obra.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (workTypeId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este tipo de obra? O respetivo template também será removido.")) return;
    setDeletingId(workTypeId);
    setError(null);
    try {
      await deleteWorkType(workTypeId);
      setWorkTypes((prev) => prev.filter((item) => item.id !== workTypeId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setWorkTypes((prev) => prev.filter((item) => item.id !== workTypeId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o tipo de obra.");
        loadWorkTypes(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (workType: WorkType) => {
    setEditing(workType);
    setDraftName(workType.name ?? "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!draftName.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setError(null);
    try {
      const updated = await updateWorkType(editing.id, {
        name: draftName.trim(),
        sortOrder: editing.sortOrder,
      });
      setWorkTypes((prev) =>
        prev
          .map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
          .sort((a, b) => a.sortOrder - b.sortOrder),
      );
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o tipo de obra.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border p-5" style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-4" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Adicionar Tipo de Obra</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-end">
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Designação *
            <input value={nome} onChange={(event) => setNome(event.target.value)} maxLength={120} placeholder="Ex: Moradia unifamiliar, Remodelação" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
          <div className="flex md:justify-end">
            <button
              type="button"
              disabled={isSaving}
              onClick={handleAdd}
              className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius)" }}
            >
              {isSaving ? "A adicionar…" : "Adicionar"}
            </button>
          </div>
        </div>
        <p className="mt-3 text-secondary-text text-sm">
          Cada tipo de obra pode ter um <strong>template</strong> com os capítulos e artigos típicos, usado para gerar a estrutura inicial de um orçamento a partir da área (m²).
        </p>
      </div>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[560px] w-full">
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Designação</th>
              <th className="px-4 py-3 w-[160px]">Linhas no template</th>
              <th className="px-4 py-3 w-[260px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : workTypes.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-secondary-text">Sem tipos de obra.</td></tr>
            ) : workTypes.map((workType) => (
              <tr key={workType.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-4 py-3 whitespace-normal break-words">{workType.name || "—"}</td>
                <td className="px-4 py-3">{workType.templateLineCount ?? 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap">
                    <button className="text-primary underline" type="button" onClick={() => navigate(`/catalogo/tipos-obra/${workType.id}`)}>Gerir template</button>
                    <button className="text-primary underline" type="button" onClick={() => openEdit(workType)}>Editar</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === workType.id} onClick={() => handleDelete(workType.id)}>{deletingId === workType.id ? "A eliminar…" : "Eliminar"}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 w-full max-w-2xl space-y-4" style={{ borderRadius: "var(--radius-card)" }}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-foreground" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Editar tipo de obra</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fechar" className="p-1 rounded hover:bg-accent/30 text-secondary-text">
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Designação *
              <input placeholder="Designação" value={draftName} maxLength={120} onChange={(e) => setDraftName(e.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-border">Cancelar</button>
              <button type="button" onClick={saveEdit} className="px-4 py-2 bg-primary text-primary-foreground">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
